import * as React from 'react';

import {change, invoice as invoiceIcon} from '@sail/icons/react/Icon';
import {useIntl as useSailIntl} from '@sail/intl';
import {defineMessages, useIntl} from 'react-intl';
import {useHover} from '@sail/react-aria';
import {Action, Badge, Divider, Icon, tokens, view} from '@sail/ui';
import type {AgentCallResponse} from '@stripe-internal/conversations-client';

import {useFormatInterval} from './hooks/useFormatInterval';
import {useWidget} from '../../../Widget/useWidget';

type InvoiceCardProps = {
  agentCallResponse: AgentCallResponse;
  onClick?: () => void;
};

const messages = defineMessages({
  cardTitle: {
    id: 'conversations_ui.invoice_card.title',
    description: 'Invoice Card title',
    defaultMessage: 'Invoice',
  },
  customerLabel: {
    id: 'conversations_ui.invoice_card.customer_label',
    description: 'Customer label',
    defaultMessage: 'Customer',
  },
  createdLabel: {
    id: 'conversations_ui.invoice_card.created_label',
    description: 'Created label',
    defaultMessage: 'Created',
  },
  statusDraft: {
    id: 'conversations_ui.invoice_card.status_draft',
    description: 'Draft status label',
    defaultMessage: 'Draft',
  },
  statusOpen: {
    id: 'conversations_ui.invoice_card.status_open',
    description: 'Open status label',
    defaultMessage: 'Open',
  },
  statusPaid: {
    id: 'conversations_ui.invoice_card.status_paid',
    description: 'Paid status label',
    defaultMessage: 'Paid',
  },
  statusPastDue: {
    id: 'conversations_ui.invoice_card.status_past_due',
    description: 'Past due status label',
    defaultMessage: 'Past due',
  },
  statusUncollectible: {
    id: 'conversations_ui.invoice_card.status_uncollectible',
    description: 'Uncollectible status label',
    defaultMessage: 'Uncollectible',
  },
  statusVoid: {
    id: 'conversations_ui.invoice_card.status_void',
    description: 'Void status label',
    defaultMessage: 'Void',
  },
});

type InvoiceNumberProps = {
  id: string;
  number: string | null | undefined;
};

const InvoiceNumber: React.FC<InvoiceNumberProps> = ({id, number}) => {
  if (number) {
    return <>{number}</>;
  }
  return <>{id}</>;
};

type InvoiceAmountProps = {
  amountDue: number | null | undefined;
  currency: string | null | undefined;
  recurring?: {interval?: string | null; interval_count?: number | null} | null;
};

const InvoiceAmount: React.FC<InvoiceAmountProps> = ({
  amountDue,
  currency,
  recurring,
}) => {
  const sailIntl = useSailIntl();
  const formatInterval = useFormatInterval();

  if (amountDue === null || amountDue === undefined || !currency) {
    return null;
  }

  const frequency = recurring?.interval
    ? formatInterval({
        interval: recurring.interval,
        interval_count: recurring.interval_count,
      })
    : null;

  return (
    <>
      {sailIntl.formatCurrency(amountDue, {currency})}
      {frequency ? (
        <view.span
          css={{
            color: 'subdued',
            font: 'body.small',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'xsmall',
            marginLeft: 'space.75',
          }}
        >
          <Icon icon={change} size="xsmall" />
          {frequency}
        </view.span>
      ) : null}
    </>
  );
};

type InvoiceStatusProps = {
  status: string | null | undefined;
};

const getInvoiceStatusBadge = (
  status: string | null | undefined,
): {
  statusMessage: (typeof messages)[keyof typeof messages];
  badgeType: 'positive' | 'negative' | 'info' | 'neutral';
} | null => {
  switch (status) {
    case 'draft':
      return {statusMessage: messages.statusDraft, badgeType: 'neutral'};
    case 'open':
      return {statusMessage: messages.statusOpen, badgeType: 'info'};
    case 'paid':
      return {statusMessage: messages.statusPaid, badgeType: 'positive'};
    case 'past_due':
      return {statusMessage: messages.statusPastDue, badgeType: 'negative'};
    case 'uncollectible':
      return {
        statusMessage: messages.statusUncollectible,
        badgeType: 'negative',
      };
    case 'void':
      return {statusMessage: messages.statusVoid, badgeType: 'neutral'};
    default:
      return null;
  }
};

const InvoiceStatus: React.FC<InvoiceStatusProps> = ({status}) => {
  const intl = useIntl();

  const result = getInvoiceStatusBadge(status);

  if (!result) {
    return null;
  }

  const {statusMessage, badgeType} = result;

  return <Badge type={badgeType}>{intl.formatMessage(statusMessage)}</Badge>;
};

type CustomerNameProps = {
  name: string;
  onClick: () => void;
};

const CustomerName: React.FC<CustomerNameProps> = ({name, onClick}) => {
  const intl = useIntl();

  return (
    <view.div
      css={{stack: 'y', gap: 'xsmall', flex: '1 1 0', minWidth: 'space.0'}}
    >
      <view.span
        css={{
          font: 'label.small.emphasized',
          color: 'subdued',
        }}
      >
        {intl.formatMessage(messages.customerLabel)}
      </view.span>
      <Action
        onPress={onClick}
        css={{
          minWidth: 'space.0',
          overflow: 'hidden',
        }}
      >
        <view.span
          css={{
            display: 'block',
            font: 'label.small',
            color: 'action.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            ':hover': {
              color: 'action.primaryHover',
            },
          }}
        >
          {name}
        </view.span>
      </Action>
    </view.div>
  );
};

type InvoiceCreatedDateProps = {
  created: number | null | undefined;
};

const InvoiceCreatedDate: React.FC<InvoiceCreatedDateProps> = ({created}) => {
  const intl = useIntl();
  const sailIntl = useSailIntl();

  if (!created) {
    return null;
  }

  const formattedDate = sailIntl.formatDateTime(new Date(created * 1000), {
    style: 'medium-year-month-day-time',
  });

  return (
    <view.div
      css={{stack: 'y', gap: 'xsmall', flex: '1 1 0', minWidth: 'space.0'}}
    >
      <view.span
        css={{
          font: 'label.small.emphasized',
          color: 'subdued',
        }}
      >
        {intl.formatMessage(messages.createdLabel)}
      </view.span>
      <view.span
        css={{
          display: 'block',
          font: 'label.small',
          color: 'subdued',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {formattedDate}
      </view.span>
    </view.div>
  );
};

interface CustomerData {
  id: string;
  name?: string | null;
}

type InvoiceCardData = {
  object: string;
  id: string;
  number?: string | null;
  status?: string | null;
  amount_due?: number | null;
  currency?: string | null;
  created?: number | null;
  customer?: CustomerData | null;
  lines?: {
    data?: Array<{
      price?: {
        recurring?: {
          interval?: string | null;
          interval_count?: number | null;
        } | null;
      } | null;
    }>;
  };
};

const toCustomerData = (customer: unknown): CustomerData | null => {
  if (!customer) {
    return null;
  }

  if (typeof customer === 'string') {
    return {id: customer};
  }

  if (typeof customer === 'object' && 'id' in customer) {
    const c = customer as Record<string, unknown>;
    return {
      id: c.id as string,
      name: 'name' in customer ? (c.name as string | null) : undefined,
    };
  }

  return null;
};

const toInvoiceCardData = (
  response: AgentCallResponse,
): InvoiceCardData | null => {
  if (typeof response !== 'object' || !('object' in response)) {
    return null;
  }

  if (response.object === 'invoice') {
    const data = response as unknown as InvoiceCardData & {customer: unknown};
    return {...data, customer: toCustomerData(data.customer)};
  }

  return null;
};

export const InvoiceCard = ({agentCallResponse, onClick}: InvoiceCardProps) => {
  const intl = useIntl();
  const widget = useWidget();
  const {hoverProps, isHovered} = useHover({});

  const invoice = toInvoiceCardData(agentCallResponse);

  if (!invoice) {
    return null;
  }

  return (
    <view.div
      data-testid="invoice-card"
      css={{
        backgroundColor: 'surface',
        display: 'flex',
        alignItems: 'center',
        borderRadius: 'medium',
        paddingTop: 'small',
        paddingBottom: 'small',
        paddingLeft: 'space.150',
        paddingRight: 'space.150',
        borderColor: 'border',
        borderWidth: 'small',
        borderStyle: 'solid',
        maxWidth: '400px',
        position: 'relative',
        transition: 'box-shadow 0.5s',
        ':hover': {
          cursor: 'pointer',
          boxShadow: tokens.shadow.large.toString(),
        },
      }}
      {...hoverProps}
      onClick={() => {
        widget.requestAction({
          name: 'navigate',
          data: {
            href: `/invoices/${invoice.id}`,
            route: {
              name: 'INVOICES_DETAIL',
              params: {id: invoice.id},
            },
          },
        });
        onClick?.();
      }}
    >
      <view.div
        css={{
          stack: 'y',
          gap: 'small',
          width: 'fill',
        }}
      >
        <view.div
          css={{
            stack: 'x',
            gap: 'space.75',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <view.div
            css={{
              stack: 'x',
              gap: 'space.75',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            <view.div
              css={{
                stack: 'x',
                gap: 'xsmall',
                alignItems: 'center',
                width: 'max',
              }}
            >
              <Icon
                icon={invoiceIcon}
                size="xsmall"
                css={{color: isHovered ? 'action.primary' : 'subdued'}}
              />
              <view.span
                css={{
                  color: 'subdued',
                  font: 'label.small.emphasized',
                }}
              >
                {intl.formatMessage(messages.cardTitle)}
              </view.span>
            </view.div>
            <Divider css={{height: 'space.125', marginY: 'auto'}} />
            <view.span
              css={{
                font: 'label.small',
                color: 'subdued',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              <InvoiceNumber id={invoice.id} number={invoice.number} />
            </view.span>
          </view.div>
          <InvoiceStatus status={invoice.status} />
        </view.div>
        <view.div css={{stack: 'x', gap: 'xsmall', alignItems: 'baseline'}}>
          <view.span
            css={{
              font: 'heading.large',
            }}
          >
            <InvoiceAmount
              amountDue={invoice.amount_due}
              currency={invoice.currency}
              recurring={
                invoice.lines?.data?.[0]?.price?.recurring
                  ? invoice.lines.data[0].price.recurring
                  : null
              }
            />
          </view.span>
        </view.div>
        <view.div
          css={{
            stack: 'x',
            gap: 'small',
            alignItems: 'flex-start',
            width: 'fill',
            display: 'flex',
            overflow: 'hidden',
          }}
        >
          {invoice.customer ? (
            <CustomerName
              name={invoice.customer.name ?? invoice.customer.id}
              onClick={() => {
                if (!invoice.customer) {
                  return;
                }

                widget.requestAction({
                  name: 'navigate',
                  data: {
                    href: `/customers/${invoice.customer.id}`,
                  },
                });
                onClick?.();
              }}
            />
          ) : null}
          <InvoiceCreatedDate created={invoice.created} />
        </view.div>
      </view.div>
    </view.div>
  );
};
